import { Fragment, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { navFor, viewForPath, type NavBadgesResponse, type NavItem, type Role, type ViewId } from '@aithora/shared';
import { CollapseIcon, NAV_ICON } from './icons';
import { NavBadge } from './NavBadge';
import { sx } from '../ui/designStyles';
import { useTheme, type ThemePref } from '../ui/theme';

export function Wordmark() {
  return (
    <div className="brand">
      <img src="/aithora-mark.svg" alt="" className="brand__mark" width={34} height={26} />
      <div className="brand__words">
        <div className="brand__name" aria-label="Aithora">
          <span aria-hidden="true">
            a&#305;thora
            <i className="brand__dot" />
          </span>
        </div>
        <div className="brand__care" aria-hidden="true" translate="no">
          Care
        </div>
      </div>
    </div>
  );
}

/**
 * V9 side menu groups (design navCareShow / navMoneyShow / navPlatShow, L534-553 and the
 * group table at L39829): daily care work first, then money, then the platform, then
 * Settings on its own. Only the order the menu draws changes here; NAV_ITEMS (and so each
 * role's landing page) keep their own order.
 */
export type NavSection = 'care' | 'money' | 'platform';
export const NAV_SECTIONS: readonly [NavSection, string][] = [
  ['care', 'Care'],
  ['money', 'Money'],
  ['platform', 'Platform'],
];
const SECTION_OF: Partial<Record<ViewId, NavSection>> = {
  overview: 'care',
  home: 'care',
  companion: 'care',
  residents: 'care',
  guardian: 'care',
  worklist: 'care',
  family: 'care',
  finance: 'money',
  capture: 'money',
  performance: 'money',
  atlasIntel: 'money',
  sales: 'money',
  pulse: 'platform',
  oversight: 'platform',
  atlas: 'platform',
  policy: 'platform',
};
/** The section a page sits under, or null for the ungrouped foot (Settings). */
export const navSectionOf = (view: ViewId): NavSection | null => SECTION_OF[view] ?? null;

/** The menu as the design draws it: each section's items, then the ungrouped items. */
export function navGroups(items: NavItem[]): { groups: { key: NavSection; label: string; items: NavItem[] }[]; rest: NavItem[] } {
  return {
    groups: NAV_SECTIONS.map(([key, label]) => ({ key, label, items: items.filter((i) => navSectionOf(i.view) === key) })).filter((g) => g.items.length > 0),
    rest: items.filter((i) => navSectionOf(i.view) === null),
  };
}

const GROUP_KEY = 'aithora.console.navGroupsClosed';
function readClosed(): Partial<Record<NavSection, boolean>> {
  try {
    const v = JSON.parse(window.localStorage.getItem(GROUP_KEY) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as Partial<Record<NavSection, boolean>>) : {};
  } catch {
    return {};
  }
}

/** The group header (design: 14px, uppercase, .1em tracking, a caret on the right). */
const GROUP_HEAD =
  'display:flex;align-items:center;gap:6px;width:100%;min-height:32px;padding:6px 11px 2px;border:0;background:transparent;cursor:pointer;font-family:inherit;font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#3C454F;text-decoration:none;text-align:left;';

export function SideNav({
  role,
  badges,
  collapsed,
  onToggleCollapsed,
  phone = false,
  open = false,
  onSignOut,
  onNavigate,
}: {
  role: Role | null;
  badges: NavBadgesResponse | null;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Phone frame (design isPhone): the nav is a drawer, shown only while `open`. */
  phone?: boolean;
  open?: boolean;
  /** Phone: Sign out moves from the top bar to the foot of the drawer. */
  onSignOut?: () => void;
  /** Any link tapped in the drawer closes it (design asideClick). */
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const [closed, setClosed] = useState<Partial<Record<NavSection, boolean>>>(readClosed);
  const { groups, rest } = navGroups(navFor(role));
  const here = viewForPath(location.pathname);
  // Headers show only with the menu open and more than one group (design: !mini && shown > 1).
  const heads = !collapsed && groups.length > 1;
  const toggle = (k: NavSection) =>
    setClosed((c) => {
      const next = { ...c, [k]: !c[k] };
      try {
        window.localStorage.setItem(GROUP_KEY, JSON.stringify(next));
      } catch {
        /* a convenience only */
      }
      return next;
    });
  const cls = `sidenav${collapsed ? ' sidenav--collapsed' : ''}${phone ? ' sidenav--phone' : ''}${phone && open ? ' sidenav--open' : ''}`;
  const link = (item: NavItem) => {
    const badge = item.badge ? badges?.badges[item.badge] : undefined;
    return (
      <li key={item.view}>
        <NavLink
          to={item.path}
          className={({ isActive }) => `navitem${isActive ? ' navitem--active' : ''}`}
          title={collapsed ? item.label : undefined}
          onClick={phone ? onNavigate : undefined}
        >
          {NAV_ICON[item.view]}
          <span className="navitem__label">{item.label}</span>
          {item.badge && (
            <NavBadge
              badgeKey={item.badge}
              badge={badge}
              variant={item.badgeStyle ?? 'count'}
              compact={collapsed}
              // design (psNavStatus): a Facility Admin's System Pulse reads their building only, drawn a size smaller
              building={item.badge === 'pulse' && role === 'ADMIN'}
            />
          )}
        </NavLink>
      </li>
    );
  };
  return (
    <aside className={cls} aria-hidden={phone && !open ? true : undefined}>
      <Wordmark />
      <nav aria-label="Main" className="sidenav__nav">
        <ul className="sidenav__list">
          {groups.map((g) => {
            // The group holding the open page always stays open (design: open = mini || !closed || act).
            const isOpen = !heads || !closed[g.key] || g.items.some((i) => i.view === here);
            return (
              <Fragment key={g.key}>
                {heads && (
                  <li data-testid={`nav-group-${g.key}`}>
                    <button type="button" className="sidenav__ghead" aria-expanded={isOpen} onClick={() => toggle(g.key)} style={sx(GROUP_HEAD)}>
                      <span style={sx('flex:1 1 auto;')}>{g.label}</span>
                      <span aria-hidden="true" style={sx('font-size:14px;')}>
                        {isOpen ? '▾' : '▸'}
                      </span>
                    </button>
                  </li>
                )}
                {isOpen && g.items.map(link)}
              </Fragment>
            );
          })}
          {rest.length > 0 && <li className="sidenav__gap" aria-hidden="true" />}
          {rest.map(link)}
        </ul>
      </nav>
      {!collapsed && !phone && <ThemeSwitch />}
      <div className="sidenav__foot">
        <button type="button" className="navitem navitem--quiet" onClick={onToggleCollapsed} aria-expanded={!collapsed} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}>
          <CollapseIcon flipped={collapsed} />
          <span className="navitem__label">Collapse</span>
        </button>
      </div>
      {phone && onSignOut && (
        <button type="button" className="sidenav__signout" onClick={onSignOut}>
          Sign out
        </button>
      )}
    </aside>
  );
}

const THEME_OPTS: Array<[ThemePref, string]> = [['light', 'Light'], ['dark', 'Dark'], ['auto', 'Auto']];

/** Light / Dark / Auto, under the nav (design thLayer + the themeOpts radio group). */
export function ThemeSwitch() {
  const { pref, setPref, device } = useTheme();
  const note = pref === 'auto' ? `Auto: matches your device (${device ? 'dark' : 'light'} now)` : `Always ${pref}`;
  return (
    <div role="radiogroup" aria-label="Appearance" title={note} style={sx('display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:3px;padding:3px;margin:0 10px 6px;border-radius:9px;background:rgba(34,39,46,.06);')}>
      {THEME_OPTS.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={pref === key}
          onClick={() => setPref(key)}
          style={sx('display:flex;align-items:center;justify-content:center;height:26px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;'
            + (pref === key ? 'background:#FFFFFF;color:#22272E;box-shadow:0 1px 2px rgba(34,39,46,.15);' : 'color:#54606C;')
            + 'border:0;cursor:pointer;font-family:inherit;padding:0;' + (pref === key ? '' : 'background:transparent;'))}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
